import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../../services/Api/Admin/adminApi";
import { AdminOrganizationApi, PayoffTransactionFormProps } from "../../../../../services/Api/Admin/adminTypes";
import { PartnerSummaryApi } from "../../../../../services/Api/Partner/partnerTypes";

interface Props {
	isShown: boolean;
	onHide: () => void;
	payoff: PartnerSummaryApi;
	fetchPayoff: () => Promise<void>;
}

const AddManualPayoffModalForm = ({ isShown, onHide, payoff, fetchPayoff }: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<PayoffTransactionFormProps>({
		criteriaMode: "all",
	});
	const {
		formState,
		formState: { errors },
		register,
		control,
		handleSubmit,
		setError,
		setValue,
	} = form;

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			await adminApi.createPayoffTransaction(Number(payoff.id), data);
			await fetchPayoff();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const searchOrganizations = async (search: string) => {
		const res = await adminApi.getAdminOrganizations({ search });
		return res.map((data) => {
			return {
				name: data.name,
				id: data.company_id,
				tax_id_no: data.company_tax_id_no,
				company_name: data.company_name,
				company_id: data.company_id,
			};
		});
	};
	const onChangeLocation = (dataObj: AdminOrganizationApi) => {
		if (dataObj) {
			setValue("client_id", dataObj.company_id);
			setValue("client_name", dataObj.name);
			setValue("client_company_name", dataObj.company_name);
			setValue("client_tax_id_no", dataObj.tax_id_no);
		}
	};
	return (
		<Modal show={isShown} onHide={onHide}>
			<FormDirty formState={formState} key="add-manual-payoff-form" onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.payoff.title.add_manual_payoff_modal_form.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<fieldset className="form-group no-gutters">
						<FormInput label={t("lib:common.word.name")} name="name" errors={errors} register={register} />
						<div className={"row rows-col-2"}>
							<div className={"col"}>
								<FormMoneyInput
									allowNegative={true}
									currency={"PLN"}
									label={t("modules.payoff.field.provision.title")}
									errors={errors}
									name="provision.amount"
									control={control}
								/>
							</div>
							<div className={"col"}>
								<FormMoneyInput
									allowNegative={true}
									currency={"PLN"}
									label={t("modules.payoff.field.price_paid.title")}
									errors={errors}
									name="price_paid.amount"
									control={control}
								/>
							</div>
						</div>
						<FormInput
							as={"textarea"}
							rows={3}
							label={t("lib:common.word.description")}
							name="description"
							errors={errors}
							register={register}
						/>
					</fieldset>
					<fieldset className="form-group no-gutters">
						<h5>Lokalizacja</h5>
						<FormSelectGroup
							control={control}
							errors={errors}
							name="client_id_search"
							getOptionValue={(opt) => opt.id}
							getOptionLabel={(opt) => opt.name}
							onChange={(_, obj) => onChangeLocation(obj)}
							label={t("lib:common.word.location")}
							loadOptions={searchOrganizations}
							data-testid="client_id_search"
						/>
						<FormInput
							label={t("modules.payoff.field.client_id.title")}
							name="client_id"
							errors={errors}
							register={register}
						/>
						<FormInput
							label={t("lib:common.word.location")}
							name="client_name"
							errors={errors}
							register={register}
						/>
						<FormInput
							label={t("common.word.company_name")}
							name="client_company_name"
							errors={errors}
							register={register}
						/>
						<FormInput
							label={t("lib:common.word.tax_id_no.title")}
							name="client_tax_id_no"
							errors={errors}
							register={register}
						/>
					</fieldset>
					<fieldset className="form-group no-gutters">
						<h5>Subskrypcja</h5>
						<FormInput
							label={"ID subskrypcji"}
							name="subscription_id"
							errors={errors}
							register={register}
						/>
						<FormInput label={"ID Proformy"} name="proforma_id" errors={errors} register={register} />
					</fieldset>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default AddManualPayoffModalForm;
